import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Lightning, 
  Plus, 
  SignOut, 
  Megaphone, 
  Users, 
  ChartLineUp,
  Clock,
  CheckCircle,
  XCircle,
  Warning,
  Plugs,
  PlugsConnected
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    engagement: 0,
    leadGen: 0
  });

  useEffect(() => {
    fetchCampaigns();
    refreshUser();
  }, [refreshUser]);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/campaigns`);
      setCampaigns(response.data);
      
      // Calculate stats
      const data = response.data;
      setStats({
        total: data.length,
        published: data.filter(c => c.status === 'published').length,
        draft: data.filter(c => c.status === 'draft').length,
        engagement: data.filter(c => c.campaign_type === 'engagement').length,
        leadGen: data.filter(c => c.campaign_type === 'lead_generation').length
      });
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'published':
        return <CheckCircle weight="fill" className="w-4 h-4 text-[#E4FF1A]" />;
      case 'draft':
        return <Clock weight="fill" className="w-4 h-4 text-[#A1A1AA]" />;
      case 'error':
        return <XCircle weight="fill" className="w-4 h-4 text-red-500" />;
      default:
        return <Warning weight="fill" className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'published':
        return 'Pubblicata';
      case 'draft':
        return 'Bozza';
      case 'error':
        return 'Errore';
      default:
        return status;
    }
  };

  const formatBudget = (cents, daily) => {
    const amount = (cents / 100).toFixed(2);
    return `€${amount}${daily ? '/giorno' : ' totale'}`;
  };

  const handleMetaConnect = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/meta/auth-url`, {
        params: { frontend_origin: window.location.origin }
      });
      window.location.href = response.data.auth_url;
    } catch (err) {
      console.error('Meta connect error:', err);
    }
  };

  const handleMetaDisconnect = async () => {
    if (!window.confirm('Vuoi scollegare il tuo account Meta? Potrai ricollegarlo in qualsiasi momento.')) return;
    try {
      await axios.delete(`${API_URL}/api/meta/disconnect`);
      await refreshUser();
    } catch (err) {
      console.error('Meta disconnect error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A]">
      {/* Header */}
      <header className="glass border-b border-white/10 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#FF2A85] flex items-center justify-center neon-glow">
              <Lightning weight="fill" className="w-6 h-6 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Unbounded' }}>
              v<span className="text-[#FF2A85]">ai</span> online
            </span>
          </Link>

          <div className="flex items-center gap-4">
            {/* Meta Connection Status */}
            <div className="hidden sm:flex items-center gap-2">
              {user?.meta_account ? (
                <button
                  data-testid="meta-status-button"
                  onClick={handleMetaDisconnect}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 border border-[#E4FF1A]/30 hover:border-[#E4FF1A]/60 transition-colors cursor-pointer"
                  title="Clicca per scollegare"
                >
                  <PlugsConnected weight="fill" className="w-4 h-4 text-[#E4FF1A]" />
                  <span className="text-xs text-[#A1A1AA]">
                    {user.meta_account.page_name || 'Meta Connesso'}
                  </span>
                  <XCircle weight="fill" className="w-3.5 h-3.5 text-[#A1A1AA] hover:text-red-400" />
                </button>
              ) : (
                <button
                  data-testid="meta-connect-header"
                  onClick={handleMetaConnect}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 border border-white/10 hover:border-[#0055FF]/50 transition-colors cursor-pointer"
                >
                  <Plugs weight="fill" className="w-4 h-4 text-[#A1A1AA]" />
                  <span className="text-xs text-[#A1A1AA]">Non connesso</span>
                </button>
              )}
            </div>

            <Button
              data-testid="new-campaign-button"
              onClick={() => navigate('/create')}
              className="bg-[#FF2A85] hover:bg-[#FF4D9B] text-white btn-glow"
            >
              <Plus weight="bold" className="w-4 h-4 mr-2" />
              Nuova Campagna
            </Button>

            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="p-2 rounded-lg hover:bg-white/5 text-[#A1A1AA] hover:text-white transition-colors"
            >
              <SignOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 
            className="text-2xl sm:text-3xl font-bold tracking-tight mb-2"
            style={{ fontFamily: 'Unbounded' }}
          >
            Ciao, {user?.name || 'Utente'}
          </h1>
          <p className="text-[#A1A1AA]">
            Gestisci le tue campagne pubblicitarie Meta
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-5 card-hover">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#FF2A85]/10 flex items-center justify-center">
                <Megaphone weight="fill" className="w-5 h-5 text-[#FF2A85]" />
              </div>
            </div>
            <p className="text-2xl font-bold mb-1" style={{ fontFamily: 'Unbounded' }}>{stats.total}</p>
            <p className="text-sm text-[#A1A1AA]">Campagne Totali</p>
          </div>

          <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-5 card-hover">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#E4FF1A]/10 flex items-center justify-center">
                <CheckCircle weight="fill" className="w-5 h-5 text-[#E4FF1A]" />
              </div>
            </div>
            <p className="text-2xl font-bold mb-1" style={{ fontFamily: 'Unbounded' }}>{stats.published}</p>
            <p className="text-sm text-[#A1A1AA]">Pubblicate</p>
          </div>

          <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-5 card-hover">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#0055FF]/10 flex items-center justify-center">
                <Users weight="fill" className="w-5 h-5 text-[#0055FF]" />
              </div>
            </div>
            <p className="text-2xl font-bold mb-1" style={{ fontFamily: 'Unbounded' }}>{stats.leadGen}</p>
            <p className="text-sm text-[#A1A1AA]">Lead Generation</p>
          </div>

          <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-5 card-hover">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <ChartLineUp weight="fill" className="w-5 h-5 text-purple-500" />
              </div>
            </div>
            <p className="text-2xl font-bold mb-1" style={{ fontFamily: 'Unbounded' }}>{stats.engagement}</p>
            <p className="text-sm text-[#A1A1AA]">Interazione</p>
          </div>
        </div>

        {/* Meta Connection CTA */}
        {!user?.meta_account ? (
          <div className="bg-[#0055FF]/10 border border-[#0055FF]/30 rounded-xl p-6 mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0055FF]/20 flex items-center justify-center">
                  <Plugs weight="fill" className="w-6 h-6 text-[#0055FF]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Connetti il tuo account Meta</h3>
                  <p className="text-sm text-[#A1A1AA]">
                    Per pubblicare campagne devi collegare il tuo account Facebook/Instagram
                  </p>
                </div>
              </div>
              <Button
                data-testid="connect-meta-button"
                onClick={handleMetaConnect}
                className="bg-[#0055FF] hover:bg-[#0066FF] text-white btn-blue-glow"
              >
                <Plugs weight="bold" className="w-4 h-4 mr-2" />
                Connetti Meta
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-[#E4FF1A]/5 border border-[#E4FF1A]/20 rounded-xl p-4 mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <PlugsConnected weight="fill" className="w-5 h-5 text-[#E4FF1A]" />
                <div>
                  <p className="text-sm font-medium">
                    Pagina: <span className="text-[#E4FF1A]">{user.meta_account.page_name || user.meta_account.page_id}</span>
                  </p>
                  <p className="text-xs text-[#A1A1AA]">
                    Ad Account: {user.meta_account.ad_account_id}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid="change-meta-account"
                  variant="outline"
                  onClick={handleMetaConnect}
                  className="text-xs border-white/10 hover:border-[#0055FF]/50"
                >
                  Cambia Account
                </Button>
                <Button
                  data-testid="disconnect-meta-button"
                  variant="outline"
                  onClick={handleMetaDisconnect}
                  className="text-xs border-white/10 hover:border-red-500/50 hover:text-red-400"
                >
                  Scollega
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Campaigns List */}
        <div className="bg-[#0C0D14] border border-white/10 rounded-xl">
          <div className="p-6 border-b border-white/10">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Unbounded' }}>
              Le tue Campagne
            </h2>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-2 border-white/20 border-t-[#FF2A85] rounded-full animate-spin mx-auto mb-4" />
              <p className="text-[#A1A1AA]">Caricamento...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#FF2A85]/10 flex items-center justify-center mx-auto mb-4">
                <Megaphone weight="fill" className="w-8 h-8 text-[#FF2A85]" />
              </div>
              <h3 className="font-semibold mb-2">Nessuna campagna</h3>
              <p className="text-sm text-[#A1A1AA] mb-6">
                Crea la tua prima campagna caricando un volantino
              </p>
              <Button
                data-testid="create-first-campaign"
                onClick={() => navigate('/create')}
                className="bg-[#FF2A85] hover:bg-[#FF4D9B] text-white btn-glow"
              >
                <Plus weight="bold" className="w-4 h-4 mr-2" />
                Crea Campagna
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {campaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  data-testid={`campaign-${campaign.id}`}
                  className="p-5 hover:bg-white/[0.02] transition-colors cursor-pointer"
                  onClick={() => navigate(`/campaign/${campaign.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        campaign.campaign_type === 'lead_generation' 
                          ? 'bg-[#0055FF]/10' 
                          : 'bg-purple-500/10'
                      }`}>
                        {campaign.campaign_type === 'lead_generation' ? (
                          <Users weight="fill" className="w-5 h-5 text-[#0055FF]" />
                        ) : (
                          <ChartLineUp weight="fill" className="w-5 h-5 text-purple-500" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">{campaign.name}</h3>
                        <div className="flex items-center gap-3 text-xs text-[#A1A1AA]">
                          <span className="capitalize">
                            {campaign.campaign_type === 'lead_generation' ? 'Lead Gen' : 'Interazione'}
                          </span>
                          <span>•</span>
                          <span>{formatBudget(campaign.budget_cents, campaign.daily_budget)}</span>
                          {campaign.created_at && (
                            <>
                              <span>•</span>
                              <span>{new Date(campaign.created_at).toLocaleDateString('it-IT')}</span>
                            </>
                          )}
                        </div>
                        {campaign.generated_headline && (
                          <p className="text-xs text-[#A1A1AA] mt-1 italic truncate max-w-xs">
                            "{campaign.generated_headline}"
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(campaign.status)}
                      <span className="text-sm text-[#A1A1AA]">
                        {getStatusText(campaign.status)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
